import React, { useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { LecturesSideBar } from './side-bar/lectures-side-bar';
import { ReportHeader } from '../reports/header/report-header';
import './lectures.page.css';
import {
  getLecturesDataOperation,
  LecturesResType,
} from './operations/get-lectures-data-operation';
import { LecturesSideBarFilterDatamodel } from './side-bar/lectures-side-bar-filter-datamodel';
import dateFormat from '../../utils/date-format';
import dateUtils from '../../utils/date-format';
import {
  getLectureStatusDisplayText,
  LectureStatus,
} from '../../api/types/programs/course/lectures/lecture-status.type';
import Colors from '../../utils/colors';

// Mock data for demonstration
const lecturesDataMock = [
  {
    program: 'הנדסת תוכנה',
    startYear: '2023',
    serialNumber: '1',
    course: 'מבוא לתכנות',
    date: '2024-03-20',
    dayOfWeek: 'א׳',
    startTime: '09:00',
    endTime: '10:30',
    classroom: 'כיתה 101',
    status: 'טרם בוצע',
  },
  {
    program: 'מדעי המחשב',
    startYear: '2022',
    serialNumber: '2',
    course: 'אלגוריתמים',
    date: '2024-03-20',
    dayOfWeek: 'א׳',
    startTime: '11:00',
    endTime: '12:30',
    classroom: 'כיתה 102',
    status: 'הושלם בהצלחה',
  },
  {
    program: 'הנדסת תוכנה',
    startYear: '2023',
    serialNumber: '3',
    course: 'תכנות מונחה עצמים',
    date: '2024-03-21',
    dayOfWeek: 'ב׳',
    startTime: '09:00',
    endTime: '10:30',
    classroom: 'כיתה 103',
    status: 'ממתין לאישור',
  },
];

const LecturesPage = () => {
  const [lecturesData, setLecturesData] = useState<LecturesResType[]>([]);
  const [loading, setLoading] = useState(false);

  const getStatusSeverity = (status: string) => {
    const severityMap: {
      [key: string]: 'success' | 'warning' | 'danger' | 'info';
    } = {
      'טרם בוצע': 'info',
      'הושלם בהצלחה': 'success',
      'ממתין לאישור': 'warning',
    };
    return severityMap[status] || 'info';
  };

  const approveButtonTemplate = (rowData: any) => {
    if (rowData.status === 'ממתין לאישור') {
      return (
        <Button
          severity="success"
          text
          onClick={() => console.log('Approve lecture:', rowData)}
        >
          אשר
        </Button>
      );
    }
    return null;
  };

  const handleSearch = async (filterData: LecturesSideBarFilterDatamodel) => {
    await getLecturesDataOperation(filterData, setLoading, setLecturesData);
  };

  const [hasMore, setHasMore] = useState(true);

  // Custom footer template with "Load More" button
  const footerTemplate = () => {
    return (
      <div style={{ height: '50px', maxHeight: '50px' }}>
        {hasMore ? (
          <Button
            label="טען עוד"
            icon="pi pi-plus"
            onClick={() =>
              setLecturesData((prevData) => [...prevData, ...lecturesDataMock])
            }
            loading={loading}
          />
        ) : (
          <span>אין יותר נתונים לטעינה</span>
        )}
      </div>
    );
  };

  const getLectureStatusStyle = (lectureStatus: LectureStatus) => {
    switch (lectureStatus) {
      case LectureStatus.FINISHED:
        return { color: Colors.blueText, fontWeight: 'bold' };
      case LectureStatus.INIT:
        return { color: Colors.textColor, fontWeight: 'bold' };
      case LectureStatus.START:
        return { color: Colors.warningText, fontWeight: 'bold' };
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <ReportHeader
        title="מפגשים"
        description="דף זה מציג את כל המפגשים במערכת, כולל פרטים על הקורס, התאריך, השעה והכיתה. ניתן לסנן את המידע באמצעות הסרגל הצדדי ולאשר מפגשים שממתינים לאישור."
        metadata={[
          { label: 'מטרת הדף', value: 'ניהול מפגשים ואישור נוכחות' },
          { label: 'תדירות עדכון', value: 'זמן אמת' },
        ]}
        category="education"
      />

      <div style={{ display: 'flex', gap: '20px' }}>
        <div
          style={{
            height: 'calc(100vh - 230px)',
            minHeight: '100%',
            width: '300px',
            minWidth: '250px',
          }}
        >
          <LecturesSideBar loading={loading} onSearch={handleSearch} />
        </div>

        {/* Table Section */}
        <DataTable
          value={lecturesData}
          /*paginator*/
          rows={10}
          rowHover
          footer={footerTemplate}
          rowClassName={() => 'cursor-pointer'}
          rowsPerPageOptions={[5, 10, 25, 50]}
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
          }
          dataKey="serialNumber"
          scrollable
          scrollHeight="calc(100vh - 352px)"
          tableStyle={{
            direction: 'rtl',
            height: '100%',
          }}
          style={{ width: '100%' }}
          className="lectures-table"
        >
          <Column
            field="branchName"
            header="שלוחות"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />

          <Column
            field="courseName"
            header="קורס"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />

          <Column
            field="abstractProgramName"
            header="תוכניות לימודים"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            field="programStartYear"
            header="שנת התחלה"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            field="annualSerialProgramNumber"
            header="מספר סידורי בשנה"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />

          <Column
            field="className"
            header="אולם"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />

          <Column
            field="className"
            header="מרצים"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
            body={(rowData: LecturesResType) => (
              <div>
                {' '}
                {rowData.lecturers
                  .map(
                    (lecturer) => `${lecturer.firstName} ${lecturer.lastName}`,
                  )
                  .join(', ')}
              </div>
            )}
          />

          <Column
            field="className"
            header="שעות"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
            body={(rowData: LecturesResType) => (
              <div>
                {dateFormat.formatTimeRangeBetweenDates(
                  rowData.lecturers[0].workingStartTime,
                  rowData.lecturers[0].workingEndTime,
                )}
              </div>
            )}
          />

          <Column
            field="studentNumberInCourse"
            header="סך תלמידים משובצים"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />

          <Column
            field="studentAttendance"
            header="סך תלמידים נוכחים"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
            body={(rowData) => (
              <label>
                {rowData.lectureStatus === LectureStatus.INIT
                  ? 'ההרצאה טרם התחילה'
                  : rowData.studentAttendance}
              </label>
            )}
          />

          {/*         <Column
            field="lectureSubject"
            header="נושא המפגש"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />*/}

          <Column
            field="lectureSubject"
            header="תאריך"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
            body={(rowData: LecturesResType) => (
              <label>
                {dateUtils.displayFormatDate(
                  rowData.lecturers[0].workingStartTime,
                )}
              </label>
            )}
          />

          <Column
            field="lectureSubject"
            header="סטטוס מפגש"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
            body={(rowData: LecturesResType) => (
              /*<div>{getLectureStatusDisplayText(rowData.lectureStatus)}</div>*/

              <span style={getLectureStatusStyle(rowData.lectureStatus)}>
                {getLectureStatusDisplayText(rowData.lectureStatus)}
              </span>
            )}
          />
        </DataTable>
      </div>
    </div>
  );
};

export default LecturesPage;
